import { useState } from 'react'
import { syncFromGoogle, syncToGoogle, clearCache } from '../api'

type SyncState = 'idle' | 'running' | 'done' | 'error'

interface SyncButtonProps {
  label: string
  description: string
  state: SyncState
  onSync: () => void
  buttonLabel?: string
}

function SyncButton({ label, description, state, onSync, buttonLabel = 'Run' }: SyncButtonProps) {
  return (
    <div
      style={{
        padding: '20px',
        background: 'rgba(255,255,255,0.04)',
        borderRadius: '8px',
        border: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>{label}</div>
      <div style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.5 }}>{description}</div>
      <button
        onClick={onSync}
        disabled={state === 'running'}
        style={{
          alignSelf: 'flex-start',
          padding: '8px 18px',
          fontSize: '13px',
          fontWeight: 500,
          borderRadius: '6px',
          background: state === 'running' ? 'rgba(255,255,255,0.08)' : 'var(--accent)',
          color: state === 'running' ? 'var(--text-muted)' : '#000',
          cursor: state === 'running' ? 'not-allowed' : 'pointer',
          transition: 'background 0.15s',
        }}
      >
        {state === 'running' ? 'Running…' : buttonLabel}
      </button>
      {state === 'done' && (
        <div style={{ fontSize: '12px', color: '#4ade80' }}>Done.</div>
      )}
      {state === 'error' && (
        <div style={{ fontSize: '12px', color: '#f87171' }}>Failed to start sync. Check backend logs.</div>
      )}
    </div>
  )
}

export default function SyncPage() {
  const [fromState,  setFromState]  = useState<SyncState>('idle')
  const [toState,    setToState]    = useState<SyncState>('idle')
  const [cacheState, setCacheState] = useState<SyncState>('idle')

  async function handleFromGoogle() {
    setFromState('running')
    try {
      await syncFromGoogle()
      setFromState('done')
    } catch {
      setFromState('error')
    }
  }

  async function handleToGoogle() {
    setToState('running')
    try {
      await syncToGoogle()
      setToState('done')
    } catch {
      setToState('error')
    }
  }

  async function handleClearCache() {
    setCacheState('running')
    try {
      await clearCache()
      setCacheState('done')
    } catch {
      setCacheState('error')
    }
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px 16px' }}>
      <div style={{ maxWidth: 520, display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px' }}>
          Data is stored in DynamoDB/S3 and synced to Google Drive on demand or hourly.
        </div>
        <SyncButton
          label="Sync from Google Drive"
          description="Import all sheets and docs from Google Drive into DynamoDB/S3. Use this to pull in changes made directly in Google."
          state={fromState}
          onSync={handleFromGoogle}
        />
        <SyncButton
          label="Sync to Google Drive"
          description="Export all data from DynamoDB/S3 back to Google Sheets and Docs. This overwrites the Google files with the current authoritative data."
          state={toState}
          onSync={handleToGoogle}
        />
        <SyncButton
          label="Clear Cache"
          description="Evict all in-memory sheet and doc caches. The next request will reload fresh data from DynamoDB."
          state={cacheState}
          onSync={handleClearCache}
          buttonLabel="Clear Cache"
        />
      </div>
    </div>
  )
}
